// CodeMirror Tengo Mode
// Based on the Tengo language specification: https://github.com/d5/tengo/blob/master/docs/tutorial.md and https://github.com/d5/tengo/blob/master/docs/syntax.md

import CodeMirror from "codemirror";

CodeMirror.defineMode("tengo", function () {
  "use strict";

  var keywords = {
    break: true,
    case: true,
    const: true,
    continue: true,
    default: true,
    else: true,
    error: true,
    export: true,
    for: true,
    func: true,
    if: true,
    import: true,
    in: true,
    immutable: true,
    return: true,
    switch: true,
    var: true,
    try: true,
    catch: true,
    finally: true,
    throw: true,
    defer: true,
  };

  var atoms = {
    true: true,
    false: true,
    nil: true,
    undefined: true,
  };

  // Note: Highlighting all built-in functions can make the code noisy.
  // They are technically identifiers that can be shadowed.
  // You can uncomment the 'builtin' styling if desired.
  var builtins = {
    len: true,
    copy: true,
    append: true,
    delete: true,
    cap: true,
    make: true,
    panic: true,
    recover: true,
    type: true,
    print: true,
    println: true,
    // Add more builtins from the Tengo spec if comprehensive highlighting is needed.
    // Example: "isError": true, "isFunction": true, etc.
  };

  var punctuationChars = /[\[\]{},();]/;

  function tokenString(quoteType) {
    return function (stream, state) {
      var escaped = false,
        next;
      while ((next = stream.next()) != null) {
        if (next == quoteType && !escaped) {
          break;
        }
        // In Tengo, raw strings (`) do not process escape sequences.
        if (quoteType != "`") {
          escaped = !escaped && next == "\\";
        }
      }
      state.tokenize = null; // Go back to base tokenizer
      return "string";
    };
  }

  function tokenChar(stream, state) {
    var escaped = false,
      next;
    var sawClosingQuote = false;
    while ((next = stream.next()) != null) {
      if (next == "'" && !escaped) {
        sawClosingQuote = true;
        break;
      }
      if (escaped) {
        escaped = false;
      } else if (next == "\\") {
        escaped = true;
      }
      // Stop if EOL is reached before closing quote for char literal
      if (stream.eol() && !sawClosingQuote) break;
    }
    state.tokenize = null;
    return "string-2"; // CodeMirror style for char literals
  }

  function tokenComment(stream, state) {
    var maybeEnd = false,
      ch;
    while ((ch = stream.next()) != null) {
      if (ch == "/" && maybeEnd) {
        state.tokenize = null;
        break;
      }
      maybeEnd = ch == "*";
    }
    return "comment";
  }

  function tokenBase(stream, state) {
    var ch = stream.next();

    // String literals: "..." or `...`
    if (ch == '"' || ch == "`") {
      state.tokenize = tokenString(ch);
      return state.tokenize(stream, state);
    }

    // Character literals: '...'
    if (ch == "'") {
      state.tokenize = tokenChar;
      return state.tokenize(stream, state);
    }

    // Comments: // or /* ... */
    if (ch == "/") {
      if (stream.eat("/")) {
        stream.skipToEnd();
        return "comment";
      }
      if (stream.eat("*")) {
        state.tokenize = tokenComment;
        return state.tokenize(stream, state);
      }
    }

    // Number literals
    // Handles: 123, 0, 0.5, .5, 1e10, 0xFF (hex), 0b101 (binary), 0600 (octal)
    if (
      /\d/.test(ch) ||
      (ch === "." && stream.peek() && /\d/.test(stream.peek()))
    ) {
      if (ch === ".") {
        // Starts with . (e.g., .5)
        stream.eatWhile(/\d/);
      } else if (ch === "0") {
        // Starts with 0 (0, 0.5, 0xFF, 0b10, 0600)
        if (stream.eat(/[xX]/)) {
          // Hexadecimal
          stream.eatWhile(/[0-9a-fA-F]/);
        } else if (stream.eat(/[bB]/)) {
          // Binary
          stream.eatWhile(/[01]/);
        } else {
          // Potential Octal (e.g. 0600, 077) or Decimal (0) or Float (0.5)
          var isOctal = true;
          var startPos = stream.pos;
          stream.eatWhile(/[0-7]/); // Greedily eat 0-7

          if (stream.pos === startPos) {
            // Only '0' was present or '0' followed by non-octal digit
            isOctal = false;
          }

          var nextChar = stream.peek();
          if (
            nextChar &&
            (nextChar === "." ||
              nextChar.toLowerCase() === "e" ||
              /[89]/.test(nextChar))
          ) {
            // If followed by float indicators or non-octal digits (8,9), it's not pure octal.
            isOctal = false;
            stream.pos = startPos; // Backtrack to just after '0'
            stream.eatWhile(/\d/); // Re-parse as decimal part if it was like 08, 09
          }

          if (!isOctal && stream.pos === startPos) {
            // If it was just '0' and nothing else consumed
            // This ensures '0' alone is treated as decimal 0, not octal.
            // If it was '08', eatWhile(/\d/) above would handle '8'.
          }
        }
      } else {
        // Starts with 1-9
        stream.eatWhile(/\d/);
      }

      // Optional float part
      if (stream.eat(".")) {
        stream.eatWhile(/\d/);
      }
      // Optional exponent part
      if (stream.eat(/[eE]/)) {
        stream.eat(/[+\-]?/);
        stream.eatWhile(/\d/);
      }
      return "number";
    }

    // Operators and Punctuation
    if (ch == ":") {
      return stream.eat("=") ? "operator" : "punctuation"; // := or :
    }
    if (ch == ".") {
      if (stream.eat(".")) {
        return stream.eat(".") ? "operator" : "operator"; // ... or .. (Tengo uses ... for variadic)
        // ".." is not a Tengo operator, but eat it as op
      }
      return "operator"; // . selector
    }

    // General operators (order can matter for multi-char ops)
    if (ch == "=") {
      return stream.eat("=") ? "operator" : "operator";
    } // == or =
    if (ch == "!") {
      return stream.eat("=") ? "operator" : "operator";
    } // != or !
    if (ch == ">") {
      if (stream.eat("=")) return "operator"; // >=
      if (stream.eat(">")) return "operator"; // >> (Bitwise right shift)
      return "operator"; // >
    }
    if (ch == "<") {
      if (stream.eat("=")) return "operator"; // <=
      if (stream.eat("<")) return "operator"; // << (Bitwise left shift)
      return "operator"; // <
    }
    if (ch == "+") {
      return stream.eat("=") ? "operator" : "operator";
    } // += or +
    if (ch == "-") {
      return stream.eat("=") ? "operator" : "operator";
    } // -= or -
    if (ch == "*") {
      if (stream.eat("=")) return "operator"; // *=
      if (stream.eat("*")) return "operator"; // ** (Exponentiation)
      return "operator"; // *
    }
    if (ch == "/") {
      // Division operator (comments handled earlier)
      return stream.eat("=") ? "operator" : "operator"; // /= or /
    }
    if (ch == "%") {
      return stream.eat("=") ? "operator" : "operator";
    } // %= or %

    if (ch == "&") {
      if (stream.eat("&")) return "operator"; // && (Logical AND)
      if (stream.eat("^")) return "operator"; // &^ (Bitwise AND NOT)
      return "operator"; // & (Bitwise AND)
    }
    if (ch == "|") {
      if (stream.eat("|")) return "operator"; // || (Logical OR)
      return "operator"; // | (Bitwise OR)
    }
    if (ch == "^") {
      return "operator";
    } // ^ (Bitwise XOR)

    if (ch == "?") {
      if (stream.eat("?")) return "operator"; // ?? (Nil-coalescing)
      return "operator"; // ? (Ternary conditional)
    }

    if (punctuationChars.test(ch)) {
      return "punctuation";
    }

    // Identifiers and keywords
    if (/[\w_]/.test(ch)) {
      // Tengo identifiers can start with _ or letter
      stream.eatWhile(/[\w\d_]/);
      var word = stream.current();
      if (keywords.hasOwnProperty(word)) {
        return "keyword";
      }
      if (atoms.hasOwnProperty(word)) {
        return "atom";
      }
      // if (builtins.hasOwnProperty(word)) { // Optional: highlight builtins
      //     return "builtin";
      // }
      return "variable"; // Default for identifiers
    }

    return null; // Unrecognized character, CodeMirror will typically just pass it through
  }

  // Interface
  return {
    startState: function () {
      return { tokenize: null }; // tokenize will be set to tokenBase or a specific tokenizer for multi-line tokens
    },

    token: function (stream, state) {
      if (stream.eatSpace()) return null; // Skip whitespace
      // If in a multi-line tokenizing state (string, comment), use that tokenizer
      if (state.tokenize) return state.tokenize(stream, state);
      // Otherwise, use the base tokenizer
      return tokenBase(stream, state);
    },

    blockCommentStart: "/*",
    blockCommentEnd: "*/",
    lineComment: "//",
    fold: "brace", // Enable folding for braces {}
    // electricChars: "{}:", // Characters that trigger re-indentation (optional)
    // indent: function(state, textAfter) { ... } // Custom indentation logic (optional)
  };
});

CodeMirror.defineMIME("text/x-tengo", "tengo");
